import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// project_instances table; ProjectDto.From in lureo-backend Data/Api/V1/Projects/ProjectsDtos.cs
export type ProjectDto = components['schemas']['ProjectDto'];
export type CreateProjectRequest = components['schemas']['CreateProjectRequest'];
export type EnsureProjectRequest = components['schemas']['EnsureProjectRequest'];

const spec = {
  uniqueId: 'project_instance_id',
  orgUniqueId: 'org_instance_id',
  userUniqueId: 'user_id',
  projectTypeUniqueId: 'project_type_id',
  displayName: 'project_instance_display_name',
  description: 'project_instance_description',
  sourceName: 'project_instance_source_name',
  category: 'project_instance_category',
  tags: 'project_instance_tags',
  locationInfo: { legacy: 'project_instance_location_info', as: 'jsonText' },
  coverImgUrl: 'project_instance_cover_img_url',
  coverImgUrls: 'project_instance_cover_img_urls',
  coverImages: { legacy: 'project_instance_cover_images', as: 'jsonText' },
  mapImgUrl: 'project_instance_map_img_url',
  // isPublished, totalScenes and totalDuration are server-maintained (refreshProjectSummary); null totals read as 0
  isPublished: 'project_instance_is_published',
  totalScenes: 'project_instance_total_scenes',
  totalDuration: 'project_instance_total_duration',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
} as const satisfies FieldSpec<ProjectDto>;

const map = fieldMap<ProjectDto, typeof spec>('Project', spec);

export type LegacyProject = LegacyRow<typeof spec, ProjectDto>;

/** updateProject accepts these. */
export const PROJECT_PATCH_FIELDS = ['displayName', 'description', 'sourceName', 'tags', 'locationInfo', 'mapImgUrl'] as const;

// The server sets the owner, org and timestamps of a new project
const CREATE_SERVER_SET = ['user_id', 'org_instance_id', 'created_at', 'updated_at'] as const;

export const projectToLegacy = (dto: ProjectDto): LegacyProject => map.toLegacy(dto);

/** createProject body from a legacy insert row; project_instance_id is required. */
export function projectCreateBody(row: Partial<LegacyProject>): CreateProjectRequest {
  const body = map.toBody(row, ['uniqueId', 'displayName', 'category'], CREATE_SERVER_SET);
  return { ...body, uniqueId: requireId('Project', 'project_instance_id', body.uniqueId) };
}

/** ensureProject body (find the caller's project by category, else display name, or create it with this id). */
export function projectEnsureBody(row: Partial<LegacyProject>): EnsureProjectRequest {
  const body = map.toBody(row, ['uniqueId', 'displayName', 'category'], CREATE_SERVER_SET);
  return { ...body, uniqueId: requireId('Project', 'project_instance_id', body.uniqueId) };
}

/** updateProject body from a legacy partial update; updated_at is stamped by the server. */
export const projectPatchBody = (changes: Partial<LegacyProject>) => map.toBody(changes, PROJECT_PATCH_FIELDS, ['updated_at']);
